import React, { FC, useEffect, useMemo, useState } from "react";
import { Confirm } from "semantic-ui-react";
import { guid, isGuid } from "../../../Helpers";
import {
  currencyFormatter,
  IFetchError,
  MethodTypes,
  useFetchData,
  useParseRouteActions,
} from "../../../helpers/GlobalHelper";
import { GlobalConstants } from "../../constants/GlobalConstants";
import LmsActionDropdown from "../ui/LmsActionDropdown";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import LmsTable from "../ui/LmsTable";
import LmsTabPages from "../ui/LmsTabPages";

enum SUBPAGE_ROUTE {
  ADD_LOAN_TRANSACTION = "add-loan-transaction",
  EDIT_LOAN_TRANSACTION = "edit-loan-transaction",
  ADD_LOAN_PAYMENT = "add-loan-payment",
  EDIT_LOAN_PAYMENT = "edit-loan-payment",
  ADD_LOANS = "add-loans",
  EDIT_LOANS = "edit-loans",
  ADD_PAYOFF_QUOTE = "add-payoff-quote",
  EDIT_PAYOFF_QUOTE = "edit-payoff-quote",
  PRINCIPAL_PAYMENT_TO_LP = "principal-payment-to-lp",
  INTEREST_PAYMENT_TO_LP = "interest-payment-to-lp",
}

enum PAYMENT_TABLE {
  LOAN_TRASACTION = "loanTransaction",
  LOAN_PAYMENT = "loanPayment",
  LOANS = "loans",
  PAYOFF_QUOTES = "payoffQuote",
}

enum QUERY_PARAM {
  ORGANIZATION_ID = "organizationId",
  PAYMENT_ID = "purchasingCompanyPaymentId",
}

type QueryParamType = string | null;

interface IQueryParams {
  organizationId: QueryParamType;
  purchasingCompanyPaymentId: QueryParamType;
}

interface SubpageProps extends IQueryParams {
  id?: string;
}

interface CurrentSubpageProps extends SubpageProps {
  component: FC<SubpageProps>;
}

interface ISharedPurchaseTable {
  header: string;
  apiSuffix: string;
  deleteSuffix: string;
  addSubpage: SUBPAGE_ROUTE;
  editSubpage: SUBPAGE_ROUTE;
}

interface SharedPurchasePaymentTableProps extends ISharedPurchaseTable {
  purchasingCompanyPaymentId: string;
  displayOnly?: true;
}

const PURCHASE_PAYMENT_TABLE = {
  [PAYMENT_TABLE.LOAN_TRASACTION]: {
    header: "Loan Transaction",
    addSubpage: SUBPAGE_ROUTE.ADD_LOAN_TRANSACTION,
    editSubpage: SUBPAGE_ROUTE.EDIT_LOAN_TRANSACTION,
    apiSuffix: "/payment/payment-loan-transactions",
    deleteSuffix: "/payment/purchasing-company-payment-loan-transactions",
  },
  [PAYMENT_TABLE.LOAN_PAYMENT]: {
    header: "Loan Payment",
    addSubpage: SUBPAGE_ROUTE.ADD_LOAN_PAYMENT,
    editSubpage: SUBPAGE_ROUTE.EDIT_LOAN_PAYMENT,
    apiSuffix: "/payment/payment-loan-payments",
    deleteSuffix: "/payment/purchasing-company-payment-loan-payments",
  },
  [PAYMENT_TABLE.LOANS]: {
    header: "Loans",
    addSubpage: SUBPAGE_ROUTE.ADD_LOANS,
    editSubpage: SUBPAGE_ROUTE.EDIT_LOANS,
    apiSuffix: "/payment/payment-loans",
    deleteSuffix: "/payment/purchase-loans",
  },
  [PAYMENT_TABLE.PAYOFF_QUOTES]: {
    header: "Payoff Quote",
    addSubpage: SUBPAGE_ROUTE.ADD_PAYOFF_QUOTE,
    editSubpage: SUBPAGE_ROUTE.EDIT_PAYOFF_QUOTE,
    apiSuffix: "/payment/payment-payoff-quotes",
    deleteSuffix: "/payment/payment-payoff-quotes",
  },
};

const PAYMENT_DELETE_SUFFIX = "/payment/delete-purchase-payment";
const OWNER_OPTIONS_SUFFIX = "/metadata/list-purchasing-company";
const PAGE_BASE_PATH = "/accounting-purchasing-company-payments";
const subpagesValues = Object.values(SUBPAGE_ROUTE);
const getSubpagePath = (subpage: SUBPAGE_ROUTE) => `${PAGE_BASE_PATH}/${subpage}`;
const getSharedActions = (hasId: boolean) =>
  hasId
    ? ["save", "save-close", "delete", "close"]
    : ["save-new", "save-continue", "save-close", "close"];

const usePurchaseCompanyPayment = ({ purchasingCompanyPaymentId, enabled = true }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(
    enabled ? `/payment/purchase-payment/${purchasingCompanyPaymentId}` : ""
  );

  return {
    data: "isActive" in data ? { ...data, voided: !data.isActive } : data,
    isLoading,
    error,
  };
};

const usePurchasePaymentForm = ({
  id,
  purchasingCompanyPaymentId,
  data,
  editSubpageRoute,
  deleteKey,
}) => {
  const {
    data: paymentData,
    isLoading,
    error,
  } = usePurchaseCompanyPayment({
    purchasingCompanyPaymentId,
    enabled: !id,
  });

  const dataFormatterFunc = (data) => ({
    purchasingCompanyPaymentDisplay: paymentData.display,
    ...data,
    id: id || guid(),
    purchasingCompanyPaymentId,
  });

  return {
    error,
    isLoading,
    dataFormatterFunc,
    actions: getSharedActions(!!id),
    submitMethod: (id ? "PUT" : "POST") as MethodTypes,
    deleteSuffix: PURCHASE_PAYMENT_TABLE[deleteKey].deleteSuffix,
    saveAndContinueSuffix: `${getSubpagePath(editSubpageRoute)}/{id}?${
      QUERY_PARAM.PAYMENT_ID
    }=${purchasingCompanyPaymentId}`,
    data: dataFormatterFunc(data),
  };
};

const setOrganizationField = (form: BCFormController): void => {
  form.addCenteredGroup([
    form.getDropdownField("Organization", OWNER_OPTIONS_SUFFIX, {
      required: true,
      disabled: true,
    }),
  ]);
};

const LPPrincipalPaymentForm: FC<SubpageProps> = ({ organizationId }) => {
  const [confirmOptions, setConfirmOptions] = useState<{
    message: string;
    handleConfirm: any;
  } | null>(null);

  const myForm = new BCFormController({ organizationId });
  setOrganizationField(myForm);
  myForm.addCenteredGroup([
    myForm.getDropdownField("Bank Organization", "/bankaccount/list-banks"),
  ]);

  myForm.addCenteredGroup([myForm.getCurrencyField("Transaction Amount", { required: true })]);
  myForm.addCenteredGroup([
    myForm.getCalendarField("Transaction Effective Date", { required: true }),
  ]);

  myForm.addCenteredGroup([
    myForm.getDropdownField("Loans", "payment/list-onesharp-loans", {
      dropdownMultiple: true,
      name: "loanIds",
    }),
  ]);

  const handleSubmit = (inputs, options) => {
    const handleConfirm = () => {
      options.handleSubmit("/payment/principal-payment-to-lp", {
        method: "POST",
        data: inputs,
        buttonText: options.buttonText,
        onSuccessFunc: () => history.back()
      });
    };

    options.handleSubmit("/payment/check-payoff-loans", {
      method: "POST",
      data: inputs.loanIds,
      showSuccessUiMessage: false,
      buttonText: options.buttonText,
      onSuccessFunc: (message) => {
        if (typeof message === "string") {
          setConfirmOptions({
            handleConfirm: () => {
              setConfirmOptions(null);
              handleConfirm();
            },
            message: `${message} Are you sure you want to continue?`,
          });
        } else {
          handleConfirm();
        }
      },
    });
  };

  return (
    <>
      {confirmOptions !== null && (
        <Confirm
          open
          size="tiny"
          content={confirmOptions.message}
          onConfirm={confirmOptions.handleConfirm}
          onCancel={() => setConfirmOptions(null)}
        />
      )}
      <LmsFormGenerator
        myForm={myForm}
        handleSubmit={handleSubmit}
        actions={[{ action: "save-close", text: "Submit" }, "close"]}
      />
    </>
  );
};

const LPInterestPaymentForm: FC<SubpageProps> = ({ organizationId }) => {
  const [{ fetchData, setError, error }] = useFetchData();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({
    organizationId,
    lpInterestTempId: "",
    lpinterestPaymentTempDetails: [],
  });

  const myForm = new BCFormController(data, {
    submitMethod: "PUT",
    initIsDisabled: true,
    submitSuffix: `/payment/submit-lp-interest-temp/${data.lpInterestTempId}`,
  });

  setOrganizationField(myForm);
  myForm.addCenteredGroup([
    myForm.getCalendarField("Transaction Effective Date", { required: true }),
  ]);

  if (data?.lpinterestPaymentTempDetails) {
    myForm.addComponent(<LmsTable
      data={data?.lpinterestPaymentTempDetails}
      displayOnlyProps={["masterBorrower", "spe", "loanNumber", "lppercentage", "interestDueToLp"]}
      isDisabled 
    />);
  }

  useEffect(() => {
    (async () => {
      try {
        const { data: lpInterestTempData, error } = await fetchData(
          `/payment/lp-interest-temp/${organizationId}`,
          {
            fetchMethod: "POST",
          }
        );

        if (error) throw error;
        const { id: lpInterestTempId } = lpInterestTempData;
        const { data, error: initialDataError } = await fetchData(
          `/payment/lp-interest-temp/${lpInterestTempId}`
        );

        if (initialDataError) throw initialDataError;
        setData({ ...data, lpInterestTempId });
      } catch (e) {
        setError(e as IFetchError);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <LmsFormGenerator
      error={error}
      isLoading={isLoading}
      myForm={myForm}
      actions={[
        ...(data.lpInterestTempId ? [{ action: "save-close", text: "Submit" }] : []),
        "close",
      ]}
      componentProps={{
        interestPaymentTable: {
          data: data.lpinterestPaymentTempDetails,
        },
      }}
    />
  );
};

const setPurchasingCompanyPaymentField = (form) => {
  form.addCenteredGroup([
    form.getDropdownDisplayField("Purchasing Company Payment", {
      name: "purchasingCompanyPaymentDisplay",
    }),
  ]);
};

const LoanTransactionDetailsLabel = ({ loanTransactionId }) => {
  const [{ data = {}, isLoading, error }] = useFetchData(
    loanTransactionId ? `/loans/loan-transaction-details/${loanTransactionId}` : ""
  );

  return loanTransactionId && "amount" in data && !(isLoading || error) ? (
    <p>
      {[
        ["A Piece", "apiece"],
        ["Funded Amount", "fundedAmount"],
        ["Pending Amount", "amount"],
      ].map(([label, name]) => (
        <React.Fragment key={`${label}-${data[name]}`}>
          {label}: <span style={{ color: "red" }}>{currencyFormatter(+data[name])} </span>
        </React.Fragment>
      ))}
    </p>
  ) : null;
};

const LoanTransactionForm: FC<SubpageProps> = ({ id, purchasingCompanyPaymentId }) => {
  const [loanTransactionId, setLoanTransactionId] = useState<string | null>(null);

  const [{ data: loanTransactionData = {}, isLoading, error }] = useFetchData(
    id ? `/payment/purchasing-company-payment-loan-transactions/${id}` : ""
  );

  const {
    data,
    actions,
    isLoading: isPaymentLoading,
    error: paymentError,
    ...controllerOptions
  } = usePurchasePaymentForm({
    id,
    data: loanTransactionData,
    purchasingCompanyPaymentId,
    deleteKey: PAYMENT_TABLE.LOAN_TRASACTION,
    editSubpageRoute: SUBPAGE_ROUTE.EDIT_LOAN_TRANSACTION,
  });

  const myForm = new BCFormController(data, {
    submitSuffix: "/payment/purchasing-company-payment-loan-transaction",
    ...controllerOptions,
  });

  setPurchasingCompanyPaymentField(myForm);
  myForm.addCenteredGroup([
    myForm.getDropdownField(
      "Loan Transaction",
      `/payment/list-loan-transactions/${purchasingCompanyPaymentId}/${id ? "true" : ""}`,
      {
        required: true,
        onFieldChanged: ({ value }) => {
          setLoanTransactionId(value);
        },
      }
    ),
  ]);

  myForm.addCenteredGroup(
    [
      myForm.getComponent(LoanTransactionDetailsLabel, {
        name: "loanTransactionDetails",
      }),
    ],
    { isHiddenFunc: ({ inputs }) => !inputs.loanTransactionId }
  );

  myForm.addCenteredGroup([myForm.getCurrencyField("Transaction Amount", { required: true })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Description")]);

  useEffect(() => {
    if (loanTransactionData.loanTransactionId) {
      setLoanTransactionId(loanTransactionData.loanTransactionId);
    }
  }, [loanTransactionData]);

  return (
    <LmsFormGenerator
      myForm={myForm}
      actions={actions}
      error={error || paymentError}
      isLoading={isLoading || isPaymentLoading}
      componentProps={{
        loanTransactionDetails: {
          loanTransactionId,
        },
      }}
    />
  );
};

const LoanPaymentForm: FC<SubpageProps> = ({ id, purchasingCompanyPaymentId }) => {
  const [{ data: loanPaymentData = {}, isLoading, error }] = useFetchData(
    id ? `/payment/purchasing-company-payment-loan-payment/${id}` : ""
  );

  const {
    data,
    actions,
    isLoading: isPaymentLoading,
    error: paymentError,
    ...controllerOptions
  } = usePurchasePaymentForm({
    id,
    data: loanPaymentData,
    purchasingCompanyPaymentId,
    deleteKey: PAYMENT_TABLE.LOAN_PAYMENT,
    editSubpageRoute: SUBPAGE_ROUTE.EDIT_LOAN_PAYMENT,
  });

  const myForm = new BCFormController(
    { ...data, purchasingCompanyPaymentId },
    {
      submitSuffix: "/payment/purchasing-company-payment-loan-payment",
      ...controllerOptions,
    }
  );

  setPurchasingCompanyPaymentField(myForm);
  myForm.addCenteredGroup([myForm.getDropdownField("Loan Payment", "/payment/list-loan-payments")]);
  myForm.addCenteredGroup([myForm.getCurrencyField("Transaction Amount", { required: true })]);
  myForm.addCenteredGroup([myForm.getTextareaField("Description")]);

  return (
    <LmsFormGenerator
      myForm={myForm}
      actions={actions}
      error={error || paymentError}
      isLoading={isLoading || isPaymentLoading}
    />
  );
};

const LoansForm: FC<SubpageProps> = ({ id, purchasingCompanyPaymentId }) => {
  const [{ data: loansData = { isActive: true }, isLoading, error }] = useFetchData(
    id ? `/payment/payment-loan/${id}` : ""
  );

  const {
    data,
    actions,
    isLoading: isPaymentLoading,
    error: paymentError,
    ...controllerOptions
  } = usePurchasePaymentForm({
    id,
    data: loansData,
    purchasingCompanyPaymentId,
    deleteKey: PAYMENT_TABLE.LOANS,
    editSubpageRoute: SUBPAGE_ROUTE.EDIT_LOANS,
  });

  const myForm = new BCFormController(data, {
    submitSuffix: "/payment/payment-loans",
    ...controllerOptions,
  });

  setPurchasingCompanyPaymentField(myForm);
  myForm.addCenteredGroup([
    myForm.getDropdownField("Loan", "/payment/list-onesharp-loans", { required: true }),
  ]);

  myForm.addCenteredGroup([myForm.getCurrencyField("Transaction Amount", { required: true })]);
  myForm.addCenteredGroup([myForm.getCheckboxField("Is Active")]);
  myForm.addCenteredGroup([myForm.getTextareaField("Description")]);

  return (
    <LmsFormGenerator
      myForm={myForm}
      actions={actions}
      error={error || paymentError}
      isLoading={isLoading || isPaymentLoading}
    />
  );
};

const PayoffQuoteForm: FC<SubpageProps> = ({ id, purchasingCompanyPaymentId }) => {
  const [{ data: loansData = { isActive: true }, isLoading, error }] = useFetchData(
    id ? `/payment/payment-payoff-quote/${id}` : ""
  );

  const {
    data,
    actions,
    isLoading: isPaymentLoading,
    error: paymentError,
    ...controllerOptions
  } = usePurchasePaymentForm({
    id,
    data: loansData,
    purchasingCompanyPaymentId,
    deleteKey: PAYMENT_TABLE.PAYOFF_QUOTES,
    editSubpageRoute: SUBPAGE_ROUTE.EDIT_PAYOFF_QUOTE,
  });

  const myForm = new BCFormController(data, {
    submitSuffix: "/payment/payment-payoff-quote",
    ...controllerOptions,
  });

  setPurchasingCompanyPaymentField(myForm);
  myForm.addCenteredGroup([
    myForm.getDropdownField("Payoff Quote", "/payment/list-payoff-quotes", { required: true }),
  ]);

  myForm.addCenteredGroup([
    myForm.getCurrencyField("Transaction Amount", { required: true }),
    myForm.getCheckboxField("Is Active"),
  ]);

  myForm.addCenteredGroup([myForm.getTextareaField("Description")]);

  return (
    <LmsFormGenerator
      myForm={myForm}
      actions={actions}
      error={error || paymentError}
      isLoading={isLoading || isPaymentLoading}
    />
  );
};

const PurchasingCompanyPaymentTable = () => {
  const [owner, setOwner] = useState({});

  const handleSelectOwner = (_, { value }) => {
    setOwner({ [OWNER_OPTIONS_SUFFIX]: value, id: "id" });
  };

  const getOwnerPath = (subpage: SUBPAGE_ROUTE) =>
    `${getSubpagePath(subpage)}?${QUERY_PARAM.ORGANIZATION_ID}=${owner[OWNER_OPTIONS_SUFFIX]}`;

  return (
    <LmsTable
      apiSuffix="/payment/purchase-payments"
      deleteSuffix={PAYMENT_DELETE_SUFFIX}
      rowBackgroundColor="pink"
      rowBackgroundColorConditionFunc={(row) => row.voided === "Yes"}
      actionButtons={[
        "add",
        "edit",
        "remove",
        "export",
        <LmsActionDropdown
          label="Owner"
          values={owner}
          handleChange={handleSelectOwner}
          optionsSuffix="/metadata/list-purchasing-company"
        />,
        {
          text: "Principal Payment to LP",
          icon: GlobalConstants.Icons.ADD,
          isDisabled: !owner[OWNER_OPTIONS_SUFFIX],
          path: getOwnerPath(SUBPAGE_ROUTE.PRINCIPAL_PAYMENT_TO_LP),
        },
        {
          text: "Interest Payment to LP",
          icon: GlobalConstants.Icons.ADD,
          isDisabled: !owner[OWNER_OPTIONS_SUFFIX],
          path: getOwnerPath(SUBPAGE_ROUTE.INTEREST_PAYMENT_TO_LP),
        },
        {
          text: "Void",
          action: GlobalConstants.TableDefaultActions.SUBMIT_SELECTED,
          submitSuffix: "payment/void-purchasing-payment",
          isDisabled: (checkedItems) => checkedItems?.length < 1,
          method: "POST",
          refreshDataOnSuccess: true,
          confirmationMessage: "Are you sure you want to Void the selected record(s)?",
        },
      ]}
      columnLabelsDictionary={{
        organization: "Purchasing Company",
        principalPaymentToLp: "Principal Payment to LP",
        bankOrganization: "Bank"
      }}
      columnClickOptions={{
        display: { path: "accounting-purchasing-company-payments/edit", idProp: "id" }
      }}
    />
  );
};

const SharedPurchasePaymentTable: FC<SharedPurchasePaymentTableProps> = ({
  apiSuffix,
  addSubpage,
  editSubpage,
  deleteSuffix,
  purchasingCompanyPaymentId,
  header,
  displayOnly
}) => {
  const editPath = `${getSubpagePath(editSubpage)}/{id}?${
    QUERY_PARAM.PAYMENT_ID
  }=${purchasingCompanyPaymentId}`;

  return (
    <LmsTable
      apiSuffix={apiSuffix}
      onClickPath={editPath}
      deleteSuffix={deleteSuffix}
      actionButtons={(displayOnly && header !== "Loan Payment") ? ["export"] : [
        {
          action: "add",
          path: `${getSubpagePath(addSubpage)}?${
            QUERY_PARAM.PAYMENT_ID
          }=${purchasingCompanyPaymentId}`,
        },
        {
          action: "edit",
          path: editPath,
        },
        "remove",
        "export",
      ]}
    />
  );
};

const PurchasingCompanyPaymentForm = ({ purchasingCompanyPaymentId }) => {
  const hasPurchasingCompanyPaymentId = !!purchasingCompanyPaymentId;
  const { data, isLoading, error } = usePurchaseCompanyPayment({
    purchasingCompanyPaymentId,
    enabled: hasPurchasingCompanyPaymentId,
  });

  const myForm = new BCFormController(data, {
    deleteSuffix: PAYMENT_DELETE_SUFFIX,
    submitSuffix: "/payment/purchasing-company-payment",
    submitMethod: hasPurchasingCompanyPaymentId ? "PUT" : "POST",
    dataFormatterFunc: (data) => ({ ...data, isActive: !data.voided }),
  });

  myForm.addCenteredGroup([
    myForm.getDropdownField("Purchasing Company", "/metadata/list-purchasing-company", {
      name: "organizationId",
      required: true,
    }),
    myForm.getDropdownField("Bank", "/bankaccount/list-banks", { name: "bankOrganizationId" }),
  ]);

  myForm.addCenteredGroup([
    myForm.getInputField("Transaction Amount", { fieldType: "dollar", required: true }),
    myForm.getCalendarField("Transaction Effective Date", { required: true }),
  ]);

  myForm.addCenteredGroup([
    myForm.getCheckboxField("Transaction Posted", { inline: true }),
    myForm.getCalendarField("Transaction Posted On"),
  ]);

  myForm.addCenteredGroup([
    myForm.getCheckboxField("Voided", { inline: true, disabled: data.voided }),
  ]);

  myForm.addCenteredGroup([myForm.getTextareaField("Description")]);

  return (
    <>
      <LmsFormGenerator
        error={error}
        myForm={myForm}
        isLoading={isLoading}
        actions={getSharedActions(hasPurchasingCompanyPaymentId)}
      />
      {(hasPurchasingCompanyPaymentId && !isLoading) && (
        <LmsTabPages
          tabPages={Object.values(PURCHASE_PAYMENT_TABLE).reduce(
            (tables, props) => ({
              ...tables,
              [props.header]: (
                <SharedPurchasePaymentTable
                  {...props}
                  apiSuffix={`${props.apiSuffix}/${purchasingCompanyPaymentId}`}
                  purchasingCompanyPaymentId={purchasingCompanyPaymentId}
                  displayOnly={data.voided}
                />
              ),
            }),
            {
              "Purchase Quote": (
                <LmsTable
                  actionButtons={["export"]}
                  apiSuffix={`/payment/payment-purchase-quotes/${purchasingCompanyPaymentId}`}
                />
              ),
            }
          )}
        />
      )}
    </>
  );
};

const subpageComponents = new Map<SUBPAGE_ROUTE, FC<SubpageProps>>([
  [SUBPAGE_ROUTE.PRINCIPAL_PAYMENT_TO_LP, LPPrincipalPaymentForm],
  [SUBPAGE_ROUTE.INTEREST_PAYMENT_TO_LP, LPInterestPaymentForm],
]);

[
  {
    form: LoanTransactionForm,
    routes: [SUBPAGE_ROUTE.ADD_LOAN_TRANSACTION, SUBPAGE_ROUTE.EDIT_LOAN_TRANSACTION],
  },
  {
    form: LoanPaymentForm,
    routes: [SUBPAGE_ROUTE.ADD_LOAN_PAYMENT, SUBPAGE_ROUTE.EDIT_LOAN_PAYMENT],
  },
  {
    form: LoansForm,
    routes: [SUBPAGE_ROUTE.ADD_LOANS, SUBPAGE_ROUTE.EDIT_LOANS],
  },
  {
    form: PayoffQuoteForm,
    routes: [SUBPAGE_ROUTE.ADD_PAYOFF_QUOTE, SUBPAGE_ROUTE.EDIT_PAYOFF_QUOTE],
  },
].forEach(({ routes, form }) => {
  routes.forEach((route) => {
    subpageComponents.set(route, form);
  });
});

const SUBPAGES_REQUIRES_PARAM = {
  [QUERY_PARAM.ORGANIZATION_ID]: [
    SUBPAGE_ROUTE.PRINCIPAL_PAYMENT_TO_LP,
    SUBPAGE_ROUTE.INTEREST_PAYMENT_TO_LP,
  ],
  [QUERY_PARAM.PAYMENT_ID]: [
    SUBPAGE_ROUTE.ADD_LOAN_TRANSACTION,
    SUBPAGE_ROUTE.EDIT_LOAN_TRANSACTION,
    SUBPAGE_ROUTE.ADD_LOAN_PAYMENT,
    SUBPAGE_ROUTE.EDIT_LOAN_PAYMENT,
    SUBPAGE_ROUTE.ADD_LOANS,
    SUBPAGE_ROUTE.EDIT_LOANS,
    SUBPAGE_ROUTE.ADD_PAYOFF_QUOTE,
    SUBPAGE_ROUTE.EDIT_PAYOFF_QUOTE,
  ],
};

const CurrentSubpage: FC<CurrentSubpageProps> = ({ component: Component, ...props }) => (
  <Component {...props} />
);

const LmsAccountingPurchasingCompanyPayments = ({ params = {}, history }) => {
  const { isMainPage, isAdd, isEdit, actionLower, isSubpageAllowed, isActionAllowed, id, query } =
    useParseRouteActions(params, ["edit"], subpagesValues);

  if (!isActionAllowed) {
    history.goBack();
  }

  const queryParams = useMemo(
    () =>
      [QUERY_PARAM.ORGANIZATION_ID, QUERY_PARAM.PAYMENT_ID].reduce<IQueryParams>(
        (acc, param) => ({
          ...acc,
          [param]: query.get(param),
        }),
        { organizationId: null, purchasingCompanyPaymentId: null }
      ),
    [query]
  );

  const component = useMemo(() => subpageComponents.get(actionLower), [actionLower]);

  useEffect(() => {
    if (isSubpageAllowed) {
      Object.keys(queryParams).forEach((param) => {
        if (SUBPAGES_REQUIRES_PARAM[param].includes(actionLower) && (queryParams[param] === null || !isGuid(queryParams[param]))) {
          history.replace(PAGE_BASE_PATH);
        }
      });
    }
  }, [isSubpageAllowed, actionLower, queryParams]);

  return (
    <>
      {isMainPage && <PurchasingCompanyPaymentTable />}
      {(isAdd || isEdit) && <PurchasingCompanyPaymentForm purchasingCompanyPaymentId={id} />}
      {isSubpageAllowed && component && (
        <CurrentSubpage id={id} component={component} {...queryParams} />
      )}
    </>
  );
};

export default LmsAccountingPurchasingCompanyPayments;
